// api.js - API service with station and auth support
import store from '../store'

let currentStationSlug = null
let currentLiveAudioUrl = null

export function setStation(slug, liveAudioUrl = null) {
  currentStationSlug = slug
  currentLiveAudioUrl = liveAudioUrl
}

export function getStation() {
  return currentStationSlug
}

function getStationApiBase() {
  if (!currentStationSlug) {
    console.warn('[API] No station set')
    return '/api'
  }
  return `/api/s/${currentStationSlug}`
}

// Authenticated fetch wrapper
export async function authFetch(url, options = {}) {
  const headers = { ...options.headers }
  
  const accessToken = store.getters['auth/accessToken']
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }
  
  let res = await fetch(url, { 
    ...options, 
    headers,
    credentials: 'include'
  })
  
  if (res.status === 401) {
    const refreshed = await tryRefreshToken()
    if (refreshed) {
      const newToken = store.getters['auth/accessToken']
      headers['Authorization'] = `Bearer ${newToken}`
      res = await fetch(url, { ...options, headers, credentials: 'include' })
    } else {
      store.commit('auth/CLEAR_AUTH')
      window.location.href = '/login'
      throw new Error('Session expired')
    }
  }
  
  return res
}

async function tryRefreshToken() {
  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include'
    })
    if (res.ok) {
      const data = await res.json()
      store.dispatch('auth/setAccessToken', data.accessToken)
      return true
    }
  } catch (err) {
    // Refresh failed silently
  }
  return false
}

// Auth API
export const authApi = {
  async login(email, password) {
    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password })
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Login failed')
    }
    return res.json()
  },

  async logout() {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include'
    })
  },

  async refresh() {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include'
    })
    if (!res.ok) return null
    return res.json()
  },

  async me() {
    const res = await authFetch('/api/auth/me')
    if (!res.ok) throw new Error('Failed to get user')
    return res.json()
  },

  async changePassword(currentPassword, newPassword) {
    const res = await authFetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPassword, newPassword })
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to change password')
    }
    return res.json()
  }
}

// Invite API
export const inviteApi = {
  async getInvite(token) {
    const res = await fetch(`/api/invite/${token}`)
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Invalid invite')
    }
    return res.json()
  },

  async acceptInvite(token, { name, password }) {
    const res = await fetch(`/api/invite/${token}/accept`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, password })
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to accept invite')
    }
    return res.json()
  }
}

// Admin API
export const adminApi = {
  async getUsers() {
    const res = await authFetch('/api/admin/users')
    if (!res.ok) throw new Error('Failed to get users')
    return res.json()
  },

  async createInvite({ email, role, stationAccess }) {
    const res = await authFetch('/api/admin/users/invite', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role, stationAccess })
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to create invite')
    }
    return res.json()
  },

  async getInvites() {
    const res = await authFetch('/api/admin/invites')
    if (!res.ok) throw new Error('Failed to get invites')
    return res.json()
  },

  async deleteInvite(id) {
    const res = await authFetch(`/api/admin/invites/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete invite')
    return res.json()
  },

  async updateUser(id, { name, role, stationAccess }) {
    const res = await authFetch(`/api/admin/users/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, role, stationAccess })
    })
    if (!res.ok) {
      const data = await res.json()
      throw new Error(data.error || 'Failed to update user')
    }
    return res.json()
  },

  async deleteUser(id) {
    const res = await authFetch(`/api/admin/users/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Failed to delete user')
    return res.json()
  }
}

// Timeline API
export async function fetchTimeline(start, end) {
  const res = await authFetch(`${getStationApiBase()}/timeline?start=${start}&end=${end}`)
  return res.json()
}

export async function fetchMetadataPage(direction, { before, after, around, limit = 50 } = {}) {
  void direction
  let url
  if (around) {
    url = `${getStationApiBase()}/metadata?around=${around}&limit=${limit}`
  } else if (after) {
    url = `${getStationApiBase()}/metadata?after=${after}&limit=${limit}`
  } else if (before) {
    url = `${getStationApiBase()}/metadata?before=${before}&limit=${limit}`
  } else {
    url = `${getStationApiBase()}/metadata?before=${Date.now()}&limit=${limit}`
  }
  const res = await authFetch(url)
  return res.json()
}

export async function fetchStats() {
  const res = await authFetch(`${getStationApiBase()}/stats`)
  return res.json()
}

export async function fetchMetadataStats(days = 30, from = null, to = null, type = null) {
  let url = `${getStationApiBase()}/metadata-stats?days=${days}`
  if (from) url += `&from=${from}`
  if (to) url += `&to=${to}`
  if (type) url += `&type=${encodeURIComponent(type)}`
  const res = await authFetch(url)
  return res.json()
}

// ========== Chart Sync / Billboard ==========

export async function fetchAvailableCharts() {
  const res = await authFetch('/api/chart-sync/available')
  return res.json()
}

export async function fetchChartSyncPlaylists() {
  const res = await authFetch(`${getStationApiBase()}/chart-sync/playlists`)
  return res.json()
}

export async function addChartSyncPlaylist(chartId) {
  const res = await authFetch(`${getStationApiBase()}/chart-sync/playlists`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ chartId })
  })
  if (!res.ok) {
    const data = await res.json()
    throw new Error(data.error || 'Failed to add chart')
  }
  return res.json()
}

export async function removeChartSyncPlaylist(playlistId) {
  const res = await authFetch(`${getStationApiBase()}/chart-sync/playlists/${playlistId}`, {
    method: 'DELETE'
  })
  return res.json()
}

export async function fetchChartSync(days = 7, from = null, to = null) {
  let url = `${getStationApiBase()}/chart-sync?days=${days}`
  if (from) url += `&from=${from}`
  if (to) url += `&to=${to}`
  const res = await authFetch(url)
  return res.json()
}

export async function fetchStatus() {
  const res = await authFetch(`${getStationApiBase()}/status`)
  return res.json()
}

export async function startExport(start, end, title = '') {
  let url = `${getStationApiBase()}/export?start=${start}&end=${end}`
  if (title) {
    url += `&title=${encodeURIComponent(title)}`
  }
  const res = await authFetch(url)
  return res.json()
}

// Multi-cut export
export async function startMultiCutExport(cuts, title = '', customFields = {}) {
  const res = await authFetch(`${getStationApiBase()}/export/multi`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cuts, title, customFields })
  })
  return res.json()
}

export async function fetchExportStatus(exportId) {
  const res = await authFetch(`${getStationApiBase()}/export/${exportId}/status`)
  return res.json()
}

export function getSegmentUrl(segmentId) {
  return `${getStationApiBase()}/segment/${encodeURIComponent(segmentId)}`
}

export function getLiveStreamUrl() {
  // Use direct URL to ingest server if available
  if (currentLiveAudioUrl) {
    return currentLiveAudioUrl
  }
  // Fallback to API proxy
  const token = store.getters['auth/accessToken']
  const base = `/live/${currentStationSlug}`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

// Refresh live stream URL (gets new signed URL from API)
export async function refreshLiveStreamUrl() {
  if (!currentStationSlug) return null
  const station = await fetchStationBySlug(currentStationSlug)
  if (station && station.liveAudioUrl) {
    currentLiveAudioUrl = station.liveAudioUrl
    return currentLiveAudioUrl
  }
  return null
}

export function getExportDownloadUrl(exportId) {
  const token = store.getters['auth/accessToken']
  const base = `${getStationApiBase()}/export/${encodeURIComponent(exportId)}/download`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

// ── Social Video — new direct-to-Spaces upload flow ──────────────────────

/**
 * Step 1 — Get a presigned PUT URL from the API for direct browser → DO Spaces upload
 */
export async function getSocialVideoUploadUrl(slug, format = 'vertical') {
  const res = await authFetch(`/api/s/${slug}/social-video/upload-url`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ format })
  })
  return res.json() // { uploadUrl, spacesKey }
}

/**
 * Step 2 — Upload WebM blob directly to DO Spaces using the presigned URL
 */
export async function uploadWebmToSpaces(uploadUrl, webmBlob) {
  const maxAttempts = 3
  let lastError
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const res = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'video/webm' },
        body: webmBlob
      })
      if (!res.ok) throw new Error(`Spaces upload failed: HTTP ${res.status}`)
      return true
    } catch (err) {
      lastError = err
      if (attempt < maxAttempts) {
        await new Promise(r => setTimeout(r, attempt * 2000))
      }
    }
  }
  throw lastError
}

/**
 * Step 3 — Notify API that WebM is in Spaces, create transcode job in MongoDB
 */
export async function queueSocialVideoTranscode(slug, spacesKey, format = 'vertical', exportId = null) {
  const res = await authFetch(`/api/s/${slug}/social-video/queue`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ spacesKey, format, exportId })
  })
  return res.json() // { jobId, status: 'queued' }
}

/**
 * Poll transcode job status
 */
export async function getSocialVideoJob(slug, jobId) {
  const res = await authFetch(`/api/s/${slug}/social-video/transcode/job/${jobId}`)
  return res.json() // { jobId, status, mp4Filename, mp4Url, error }
}

/**
 * Check if a social video already exists for an export
 */
export async function getSocialVideoStatus(slug, exportId) {
  const res = await authFetch(`/api/s/${slug}/social-video/status/${exportId}`)
  return res.json()
}

/**
 * Get download URL — API redirects to DO Spaces signed URL
 */
export function getSocialVideoDownloadUrl(slug, filename) {
  const token = store.getters['auth/accessToken']
  const base = `/api/s/${slug}/social-video/download/${filename}`
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

export async function fetchStations() {
  const res = await authFetch('/api/stations')
  return res.json()
}

export async function fetchStationBySlug(slug) {
  const res = await authFetch(`/api/stations/${slug}`)
  if (!res.ok) return null
  return res.json()
}

export async function createStation(data) {
  const res = await authFetch('/api/stations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return res.json()
}

export async function updateStation(slug, data) {
  const res = await authFetch(`/api/stations/${slug}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return res.json()
}

export async function deleteStation(slug) {
  const res = await authFetch(`/api/stations/${slug}`, { method: 'DELETE' })
  return res.json()
}

export async function validateStreamUrl(url, username = null, password = null) {
  const res = await authFetch('/api/validate-stream', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, username, password })
  })
  return res.json()
}

export async function compareStations(stationA, stationB, days = 7, from = null, to = null) {
  const params = new URLSearchParams({ stationA, stationB })
  // Only include days if not using custom date range (from/to)
  if (from && to) {
    params.append('from', from)
    params.append('to', to)
  } else {
    params.append('days', (days || 7).toString())
  }
  const res = await authFetch(`/api/compare-stations?${params}`)
  return res.json()
}

export async function getConfig() {
  const res = await fetch('/api/config')
  return res.json()
}

// ============ WEBHOOKS ============

export async function getWebhooks() {
  const res = await authFetch('/api/webhooks')
  return res.json()
}

export async function createWebhook(data) {
  const res = await authFetch('/api/webhooks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return res.json()
}

export async function updateWebhook(id, data) {
  const res = await authFetch(`/api/webhooks/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return res.json()
}

export async function deleteWebhook(id) {
  const res = await authFetch(`/api/webhooks/${id}`, { method: 'DELETE' })
  return res.json()
}

export async function testWebhook(id) {
  const res = await authFetch(`/api/webhooks/${id}/test`, { method: 'POST' })
  return res.json()
}

// ============ CUSTOM FIELDS ============

export async function getCustomFields() {
  const res = await authFetch('/api/custom-fields')
  return res.json()
}

export async function createCustomField(data) {
  const res = await authFetch('/api/custom-fields', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return res.json()
}

export async function updateCustomField(id, data) {
  const res = await authFetch(`/api/custom-fields/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  })
  return res.json()
}

export async function deleteCustomField(id) {
  const res = await authFetch(`/api/custom-fields/${id}`, { method: 'DELETE' })
  return res.json()
}

export async function updateTranscript(slug, exportId, transcriptText) {
  const res = await authFetch(`/api/s/${slug}/export/${exportId}/transcript`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transcriptText })
  })
  return res.json()
}
